import { useState } from "react";

export type SortDir = "asc" | "desc";

export interface SortState<T extends string> {
    sortKey: T | null;
    sortDir: SortDir;
    handleSort: (key: T) => void;
    applySorting: (
        items: any[],
        getValue: (item: any, key: T) => string | number,
    ) => any[];
}

export function useSorting<T extends string>(): SortState<T> {
    const [sortKey, setSortKey] = useState<T | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    const handleSort = (key: T) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const applySorting = (
        items: any[],
        getValue: (item: any, key: T) => string | number,
    ) => {
        if (!sortKey) return items;
        return [...items].sort((a, b) => {
            const av = getValue(a, sortKey);
            const bv = getValue(b, sortKey);
            if (typeof av === "string" && typeof bv === "string") {
                const cmp = av.localeCompare(bv, "de", { sensitivity: "base" });
                return sortDir === "asc" ? cmp : -cmp;
            }
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    };

    return { sortKey, sortDir, handleSort, applySorting };
}
